import { useMemo } from "react"

import { useChannelForecast, useRawForecasts, useRawForecastWeights } from "~/hooks/useForecasts"
import { getStartOfCurrentWeekUTC, getStartOfWeekDatesBetween } from "~/utils/datetime"
import { buildWeeklyForecastSeries, padAndSumCumulativeArrays } from "~/utils/forecastUtils"
import { filterDefined } from "~/utils/tsUtils"
import { type IForecasts } from "~/stores/sku-store"

function getCombinedCumulativeArray(
  variantIds: string[],
  warehouseId: string | undefined,
  forecasts: IForecasts[],
): number[] {
  const filtered = warehouseId ? forecasts.filter((f) => f.warehouseId === warehouseId) : forecasts

  const perVariant = variantIds.map((vid) => {
    const arrays = filterDefined(filtered.map((f) => f.forecasts[vid]))
    if (arrays.length === 0) return []
    return padAndSumCumulativeArrays(arrays)
  })

  return padAndSumCumulativeArrays(perVariant.filter((a) => a.length > 0))
}

export function useProductForecast(
  variantIds: string[],
  warehouseId: string | undefined,
  channel: string | undefined,
  startDate: Date,
  endDate: Date,
  skuId?: string,
): {
  forecasts: { date: Date; value: number }[] | undefined
  isLoading: boolean
} {
  const { data: rawForecasts, isLoading: rawLoading } = useRawForecasts()
  const { forecasts: channelForecastMap, isLoading: channelLoading } = useChannelForecast(
    channel,
    warehouseId,
    true,
  )
  const { data: weights } = useRawForecastWeights()

  const baseDateMs = useMemo(() => getStartOfCurrentWeekUTC().getTime(), [])
  const weekStartDates = useMemo(
    () => getStartOfWeekDatesBetween(startDate, endDate),
    [startDate, endDate],
  )
  const forecasts = useMemo(() => {
    if (skuId) {
      const skuWeight = weights?.get(skuId)

      if (channel) {
        if (!channelForecastMap || !skuWeight) return undefined
        const variantArray = channelForecastMap.get(skuWeight.variantId)
        if (!variantArray || variantArray.length === 0) return []
        const scaled = variantArray.map((v: number) => v * skuWeight.weight)
        return buildWeeklyForecastSeries(scaled, baseDateMs, weekStartDates)
      }

      if (!rawForecasts) return undefined
      const selected = warehouseId
        ? rawForecasts.filter((f) => f.warehouseId === warehouseId)
        : rawForecasts

      // Prefer preorder-adjusted SKU forecast if available
      const specialArrays = filterDefined(selected.map((f) => f.specialSkuForecasts[skuId]))
      if (specialArrays.length > 0) {
        const combined = padAndSumCumulativeArrays(specialArrays)
        return buildWeeklyForecastSeries(combined, baseDateMs, weekStartDates)
      }

      // Fall back to weighted variant forecast
      if (!skuWeight) return []
      const variantArrays = filterDefined(selected.map((f) => f.forecasts[skuWeight.variantId]))
      if (variantArrays.length === 0) return []
      const combined = padAndSumCumulativeArrays(variantArrays)
      const scaled = combined.map((v) => v * skuWeight.weight)
      return buildWeeklyForecastSeries(scaled, baseDateMs, weekStartDates)
    }

    if (channel) {
      if (!channelForecastMap) return undefined
      const arrays = variantIds
        .map((vid) => channelForecastMap.get(vid))
        .filter(Boolean) as number[][]
      if (arrays.length === 0) return []
      const combined = padAndSumCumulativeArrays(arrays)
      return buildWeeklyForecastSeries(combined, baseDateMs, weekStartDates)
    }

    if (!rawForecasts) return undefined
    const combined = getCombinedCumulativeArray(variantIds, warehouseId, rawForecasts)
    if (combined.length === 0) return []
    return buildWeeklyForecastSeries(combined, baseDateMs, weekStartDates)
  }, [
    channel,
    channelForecastMap,
    rawForecasts,
    variantIds,
    warehouseId,
    baseDateMs,
    weekStartDates,
    skuId,
    weights,
  ])

  return {
    forecasts,
    isLoading: channel ? channelLoading : rawLoading,
  }
}
